import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { apiFetch, CellTone, money, MonthCell, monthName, PartnerMonthsGrid as Grid } from './api';

/**
 * "By partner & month" (T-082): one row per partner, one column per month,
 * cases sent against cases billed. A cell opens that partner's month.
 */

const ROLE_LABEL: Record<string, string> = {
  transport: 'transport',
  crematorium: 'crematorium',
  dropoff: 'drop-off',
};

export const TONE_CHIP: Record<CellTone, 'success' | 'warning' | 'default' | 'error'> = {
  ok: 'success',
  warn: 'warning',
  mute: 'default',
  bad: 'error',
  none: 'default',
};

export const PartnerMonthsGrid = () => {
  const [grid, setGrid] = useState<Grid | null>(null);
  const [loading, setLoading] = useState(true);
  const notify = useNotify();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setGrid(await apiFetch('partner-invoices/months?count=3'));
    } catch {
      notify('Failed to load partner months', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }
  if (!grid) return null;

  return (
    <>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Partner</TableCell>
              {grid.months.map((m) => (
                <TableCell key={m}>{monthName(m)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {grid.partners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={grid.months.length + 1} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No cases sent to partners in these months.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              grid.partners.map((p) => (
                <TableRow key={p.partner_id}>
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Typography variant="body2" fontWeight="bold">
                      {p.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.roles.map((r) => ROLE_LABEL[r] || r).join(' + ')}
                      {p.self_invoicing ? ' · invoices from the portal' : ''}
                    </Typography>
                  </TableCell>
                  {grid.months.map((m) => (
                    <TableCell key={m} sx={{ minWidth: 170 }}>
                      <Cell
                        cell={p.cells[m]}
                        onOpen={() => navigate(`/partner-reconciliation/partner/${p.partner_id}/${m}`)}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="body2" color="text.secondary" mt={1.5}>
        Each cell shows <b>billed / sent</b>: cases we sent that partner in the month (by dispatch; for a drop-off
        facility, by arrival) against cases matched to one of their uploaded bills. A partner that invoices from the
        portal (Brampton) counts the cases it marked invoiced, with the ledger total it was priced at.
      </Typography>
    </>
  );
};

const Cell = ({ cell, onOpen }: { cell?: MonthCell; onOpen: () => void }) => {
  if (!cell || cell.tone === 'none') {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <ButtonBase
      onClick={onOpen}
      sx={{
        display: 'grid',
        gap: 0.5,
        justifyItems: 'start',
        justifyContent: 'start',
        width: '100%',
        textAlign: 'left',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}
    >
      <Typography variant="body2" fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {cell.billed} / {cell.sent}
      </Typography>
      <Chip size="small" label={cell.note} color={TONE_CHIP[cell.tone]} variant="outlined" />
      {cell.ledger_total != null && cell.tone !== 'ok' && (
        <Typography variant="caption" color="text.secondary">
          ledger {money(cell.ledger_total)}
        </Typography>
      )}
    </ButtonBase>
  );
};

export default PartnerMonthsGrid;
